import React, { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { useGlobalContext } from "../Config";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";

const Documentos = () => {
  const { styleProperties, baseUrl, headerText } = useGlobalContext();

  const [documentosData, setDocumentosData] = useState([]);
  const [documentosCobranza, setDocumentosCobranza] = useState([]);
  const [documentosPagados, setDocumentosPagados] = useState([]);
  const [loadingCobranza, setLoadingCobranza] = useState(false);
  const [loadingPagados, setLoadingPagados] = useState(false);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [loadingEstados, setLoadingEstados] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false); // spinner específico de DataTable

  // Filtros principales
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tipoDocumento, setTipoDocumento] = useState(null);
  const [tipoDocumentos, setTipoDocumentos] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clientesMaster, setClientesMaster] = useState([]);

  // Toggle de más filtros
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Filtros adicionales
  const [cobrador, setCobrador] = useState(null);
  const [cobradores, setCobradores] = useState([]);
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [commitFromDate, setCommitFromDate] = useState(null);
  const [commitToDate, setCommitToDate] = useState(null);
  const [estado, setEstado] = useState(null);
  const [estados, setEstados] = useState([]);
  const [vencFromDate, setVencFromDate] = useState(null);
  const [vencToDate, setVencToDate] = useState(null);

  const cobranzaRef = useRef(null);
  const pagadosRef = useRef(null);

  // Espaciados centralizados
  const rowGap = "1.25rem";
  const fieldGap = "0.5rem";

  const loadTipos = async () => {
    setLoadingTipos(true);
    try {
      const tipoData = await fetchJsonWithRetry(
        `${baseUrl}tipoDocumentos`,
        { headers: headerText },
        { retries: 1 }
      );
      const tipoOptions = (tipoData?.tipoDocumentos || []).map((t) => ({
        value: t.id,
        label: t.descripcion,
      }));
      setTipoDocumentos(tipoOptions);
    } catch (e) {
      console.error("TipoDocumentos falló:", e);
      setTipoDocumentos([]); // deja vacío pero no rompe
    } finally {
      setLoadingTipos(false);
    }
  };

  const loadEstados = async () => {
    setLoadingEstados(true);
    try {
      const estadosData = await fetchJsonWithRetry(
        `${baseUrl}estados`,
        { headers: headerText },
        { retries: 1 }
      );
      const estadosOptions = (estadosData?.estados || []).map((e) => ({
        value: e.id,
        label: e.descripcion,
      }));
      setEstados(estadosOptions);
    } catch (e) {
      console.error("Estados falló:", e);
      setEstados([]);
    } finally {
      setLoadingEstados(false);
    }
  };

  const loadBothDocumentos = async () => {
    setLoadingDocs(true);
    setLoadingCobranza(true);
    setLoadingPagados(true);
    try {
      const [dataCob, dataPag] = await Promise.all([
        fetchJsonWithRetry(
          `${baseUrl}documentos/filtro?${buildQueryParams({
            cobranza: true,
            pagados: false,
          })}`,
          { headers: headerText },
          { retries: 0 }
        ),
        fetchJsonWithRetry(
          `${baseUrl}documentos/filtro?${buildQueryParams({
            cobranza: false,
            pagados: true,
          })}`,
          { headers: headerText },
          { retries: 0 }
        ),
      ]);

      const docsCob = dataCob?.documentos || [];
      const docsPag = dataPag?.documentos || [];

      setDocumentosCobranza(docsCob);
      setDocumentosPagados(docsPag);

      // 👇 construir opciones (clientes / cobradores) a partir de AMBOS
      const allDocs = [...docsCob, ...docsPag];
      const incomingOpts = buildClienteOptionsFromDocs(allDocs);

      setClientesMaster((prev) => {
        const master = mergeClienteOptions(prev, incomingOpts);
        setClientes(ensureSelectedInOptions(incomingOpts, cliente));
        return master;
      });

      const cobradorOptions = [
        ...new Set(allDocs.map((doc) => doc.cobrador).filter(Boolean)),
      ].map((name) => ({ value: name, label: name }));
      setCobradores(cobradorOptions);
    } catch (e) {
      console.error("Documentos (ambos) falló:", e);
      setDocumentosCobranza([]);
      setDocumentosPagados([]);
      setClientes([]);
      setCobradores([]);
    } finally {
      setLoadingCobranza(false);
      setLoadingPagados(false);
      setLoadingDocs(false);
    }
  };

  // useEffect inicial
  useEffect(() => {
    loadTipos();
    loadEstados();
    loadBothDocumentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQueryParams = (overrides = {}) => {
    const params = new URLSearchParams();

    if (tipoDocumento) params.append("tipoDocumento", String(tipoDocumento));

    const isRut = (v) => /^[0-9]{1,8}-[0-9kK]$/.test(String(v).trim());
    if (cliente && isRut(cliente)) {
      params.append("cliente", normalizeRutForApi(cliente));
    }

    if (fromDate)
      params.append("fechaEmisionDesde", formatDateForApi(fromDate));
    if (toDate) params.append("fechaEmisionHasta", formatDateForApi(toDate));

    if (estado) params.append("estado", String(estado));

    if (cobrador) {
      const idCobrador = parseCobradorId(cobrador);
      if (idCobrador != null) params.append("cobrador", String(idCobrador));
    }

    if (numeroDocumento) params.append("folio", numeroDocumento);

    if (commitFromDate)
      params.append("fechaCompromisoDesde", formatDateForApi(commitFromDate));
    if (commitToDate)
      params.append("fechaCompromisoHasta", formatDateForApi(commitToDate));

    if (vencFromDate)
      params.append("fechaVencimientoDesde", formatDateForApi(vencFromDate));
    if (vencToDate)
      params.append("fechaVencimientoHasta", formatDateForApi(vencToDate));

    // defaults
    const cobranzaFlag = overrides.cobranza ?? true;
    const pagadosFlag = overrides.pagados ?? false;
    params.append("cobranza", String(cobranzaFlag));
    params.append("pagados", String(pagadosFlag));

    return params.toString();
  };

  // fetch con filtros
  const fetchDocumentsWithFilters = async () => {
    // 👉 en lugar de una sola llamada, hacemos dos
    setLoadingDocs(true);
    setLoadingCobranza(true);
    setLoadingPagados(true);
    try {
      const [resCob, resPag] = await Promise.all([
        fetch(
          `${baseUrl}documentos/filtro?${buildQueryParams({
            cobranza: true,
            pagados: false,
          })}`,
          { headers: headerText }
        ),
        fetch(
          `${baseUrl}documentos/filtro?${buildQueryParams({
            cobranza: false,
            pagados: true,
          })}`,
          { headers: headerText }
        ),
      ]);

      const [dataCob, dataPag] = await Promise.all([
        resCob.json(),
        resPag.json(),
      ]);
      const docsCob = dataCob?.documentos || [];
      const docsPag = dataPag?.documentos || [];

      setDocumentosCobranza(docsCob);
      setDocumentosPagados(docsPag);

      const allDocs = [...docsCob, ...docsPag];
      const incomingOpts = buildClienteOptionsFromDocs(allDocs);

      setClientesMaster((prev) => {
        const master = mergeClienteOptions(prev, incomingOpts);
        setClientes(ensureSelectedInOptions(incomingOpts, cliente));
        return master;
      });

      const cobradorOptions = [
        ...new Set(allDocs.map((doc) => doc.cobrador).filter(Boolean)),
      ].map((name) => ({ value: name, label: name }));
      setCobradores(cobradorOptions);
    } catch (err) {
      console.error("Error al aplicar filtros (ambos):", err);
      setDocumentosCobranza([]);
      setDocumentosPagados([]);
    } finally {
      setLoadingCobranza(false);
      setLoadingPagados(false);
      setLoadingDocs(false);
    }
  };

  // Helpers
  const navigate = useNavigate();

  const toNumber = (v) => {
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      // CLP suele venir sin decimales, pero igual limpiamos
      const cleaned = v.replace(/\./g, "").replace(/\s/g, "").replace(",", ".");
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  // ---- helpers para Excel ----
  const safeNum = (v) => {
    const n = toNumber(v);
    return Number.isFinite(n) ? n : 0;
  };

  const formatDateForExcel = (val) => {
    const dt = parseBackendDate(val);
    return dt ? dt.toFormat("dd/MM/yyyy") : "";
  };

  const buildExcelRows = (docs = []) => {
    return docs.map((d) => ({
      RUT: d?.cliente?.rut ?? "",
      Cliente: d?.cliente?.razonSocial ?? "",
      Tipo: d?.tipoDocumento?.descripcion ?? "",
      Folio: d?.folio ?? "",
      "Fecha de Pago": formatDateForExcel(d?.fechaUltimoPago),
      "Forma de Pago": d?.formaPago ?? "",
      "Fecha de Emisión": formatDateForExcel(d?.fechaEmision),
      "Fecha de Vencimiento": formatDateForExcel(d?.fechaVencimiento),
      Días: d?.dias ?? "",
      "Fecha de Compromiso": formatDateForExcel(d?.fechaCompromiso),
      Neto: safeNum(d?.neto),
      IVA: safeNum(d?.iva),
      Total: safeNum(d?.total),
      Deuda: safeNum(d?.deuda),
      Cobrador: d?.cobrador ?? "",
    }));
  };

  const autofitColumns = (ws, rows) => {
    const headers = Object.keys(rows[0] || {});
    ws["!cols"] = headers.map((h) => {
      const maxLen = Math.max(
        h.length,
        ...rows.map((r) => String(r[h] ?? "").length)
      );
      return { wch: Math.min(Math.max(maxLen + 2, 10), 40) }; // ancho entre 10 y 40
    });
  };

  // Nombre de archivo con timestamp local
  const filename = (base) => {
    const stamp = DateTime.now()
      .setZone("America/Santiago")
      .toFormat("yyyyLLdd_HHmmss");
    return `${base}_${stamp}.xlsx`;
  };

  const exportToExcel = (rows, baseName = "export") => {
    try {
      if (!rows || rows.length === 0) {
        // opcional: puedes mostrar un toast aquí
        console.warn("No hay datos para exportar");
        return;
      }
      const ws = XLSX.utils.json_to_sheet(rows);
      autofitColumns(ws, rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Datos");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([wbout], { type: "application/octet-stream" }),
        filename(baseName)
      );
    } catch (e) {
      console.error("Error al exportar Excel:", e);
    }
  };

  // ---- funciones públicas de export ----
  const exportCobranzaExcel = () => {
    const rows = buildExcelRows(documentosCobranza);
    exportToExcel(rows, "Cobranza");
  };

  const exportPagadosExcel = () => {
    const rows = buildExcelRows(documentosPagados);
    exportToExcel(rows, "Pagados");
  };

  // Limpia espacios raros de la razon social (viene con relleno a la derecha)
  const normalizeClienteNombre = (s) =>
    (s ?? "")
      .toString()
      .trim()
      .replace(/\s{2,}/g, " ");

  // Normaliza RUT para la API (sin puntos, trim, DV mayúscula)
  const normalizeRutForApi = (rut) =>
    (rut ?? "").toString().replace(/\./g, "").trim().toUpperCase();

  // Extrae el número inicial del cobrador (ej: "22 LUIS COSTA" -> 22)
  const parseCobradorId = (val) => {
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const m = val.match(/^(\d+)/); // toma los dígitos iniciales
      return m ? parseInt(m[1], 10) : null;
    }
    return null;
  };

  const buildClienteOptionsFromDocs = (docs = []) => {
    const entries = docs
      .map((doc) => {
        const rut = (doc?.cliente?.rut || "").replace(/\./g, "").toUpperCase();
        const name = normalizeClienteNombre(doc?.cliente?.razonSocial || "");
        if (!rut) return null;
        return [rut, { value: rut, label: `${name} (${rut})` }];
      })
      .filter(Boolean);
    return [...new Map(entries).values()];
  };

  const mergeClienteOptions = (prev = [], incoming = []) => {
    // prev e incoming son arrays [{value, label}]
    const map = new Map(prev.map((o) => [o.value, o]));
    incoming.forEach((o) => map.set(o.value, o));
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  };

  const ensureSelectedInOptions = (options, selected) => {
    if (!selected) return options;
    return options.some((o) => o.value === selected)
      ? options
      : mergeClienteOptions(options, [{ value: selected, label: selected }]);
  };

  const formatDateForApi = (date) => {
    if (!date) return "";
    const dt =
      date instanceof Date
        ? DateTime.fromJSDate(date, { zone: "America/Santiago" })
        : DateTime.fromISO(String(date), { zone: "America/Santiago" });
    return dt.toISODate(); // ==> "2025-09-17"
  };

  const fetchJsonWithRetry = async (
    url,
    options = {},
    { retries = 1, backoffMs = 500 } = {}
  ) => {
    let lastErr;
    for (let i = 0; i <= retries; i++) {
      try {
        const res = await fetch(url, options);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } catch (err) {
        lastErr = err;
        if (i < retries)
          await new Promise((r) => setTimeout(r, backoffMs * (i + 1)));
      }
    }
    throw lastErr;
  };

  const totalDeudaCobranza = useMemo(
    () =>
      (documentosCobranza || []).reduce(
        (acc, d) => acc + toNumber(d?.deuda),
        0
      ),
    [documentosCobranza]
  );

  const totalDeudaPagados = useMemo(
    () =>
      (documentosPagados || []).reduce((acc, d) => acc + toNumber(d?.deuda), 0),
    [documentosPagados]
  );

  const clearAdditionalFilters = () => {
    setCobrador(null);
    setNumeroDocumento("");
    setCommitFromDate(null);
    setCommitToDate(null);
    setEstado(null);
    setVencFromDate(null);
    setVencToDate(null);
  };

  const clearAll = () => {
    setTipoDocumento(null);
    setCliente(null);
    setFromDate(null);
    setToDate(null);
    clearAdditionalFilters();
    if (showMoreFilters) setShowMoreFilters(false);
    loadBothDocumentos();
  };

  const toggleMore = () => {
    if (showMoreFilters) clearAdditionalFilters();
    setShowMoreFilters((v) => !v);
  };

  const formatCurrency = (value) =>
    value?.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    });

  const parseBackendDate = (val) => {
    if (!val) return null;

    // Si viene desde Calendar como Date
    if (val instanceof Date) {
      return DateTime.fromJSDate(val, { zone: "America/Santiago" });
    }

    if (typeof val !== "string") return null;

    const trimmed = val.trim();
    // El backend manda "CLT"/"CLST" (no IANA) -> quitamos
    const cleaned = trimmed.replace(/\sCLST|\sCLT/g, "");

    // 1) Intento principal: "Tue May 30 00:00:00 2023" (día con o sin 0)
    let dt = DateTime.fromFormat(cleaned, "ccc LLL dd HH:mm:ss yyyy", {
      zone: "America/Santiago",
      locale: "en", // MUY importante: meses en inglés
    });

    if (!dt.isValid) {
      dt = DateTime.fromFormat(cleaned, "ccc LLL d HH:mm:ss yyyy", {
        zone: "America/Santiago",
        locale: "en",
      });
    }

    // 2) Fallback: ISO
    if (!dt.isValid) {
      dt = DateTime.fromISO(trimmed, { zone: "America/Santiago" });
    }

    // 3) Fallback final: Date nativo
    if (!dt.isValid) {
      const js = new Date(trimmed);
      if (!isNaN(js)) {
        dt = DateTime.fromJSDate(js, { zone: "America/Santiago" });
      }
    }

    return dt.isValid ? dt : null;
  };

  // Formateador para las celdas del DataTable
  const formatDateCell = (val) => {
    const dt = parseBackendDate(val);
    return dt ? dt.toFormat("dd/MM/yyyy") : "—";
  };

  const DateRangeInline = ({
    label,
    fromValue,
    toValue,
    onFromChange,
    onToChange,
    fromId,
    toId,
    span = 4,
    inputWidth = 180,
  }) => (
    <div style={{ gridColumn: `span ${span}` }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
          {label}
        </label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: fieldGap,
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: "#6b7280", textTransform: "lowercase" }}>
            desde
          </span>
          <Calendar
            id={fromId}
            value={fromValue}
            onChange={(e) => onFromChange(e.value)}
            dateFormat="dd/mm/yy"
            showIcon
            mask="99/99/9999"
            placeholder="dd/mm/aaaa"
            inputStyle={{ width: "100%" }}
            style={{ width: inputWidth }}
          />
          <span style={{ color: "#6b7280", textTransform: "lowercase" }}>
            hasta
          </span>
          <Calendar
            id={toId}
            value={toValue}
            onChange={(e) => onToChange(e.value)}
            dateFormat="dd/mm/yy"
            showIcon
            mask="99/99/9999"
            placeholder="dd/mm/aaaa"
            inputStyle={{ width: "100%" }}
            style={{ width: inputWidth }}
            minDate={fromValue || null}
          />
        </div>
      </div>
    </div>
  );

  const exportCobranza = () => {
    if (cobranzaRef.current) {
      cobranzaRef.current.exportExcel?.();
    }
  };

  const exportPagados = () => {
    if (pagadosRef.current) {
      pagadosRef.current.exportExcel?.();
    }
  };

  const ButtonsRow = () => (
    <div
      style={{
        display: "flex",
        gap: fieldGap,
        justifyContent: "flex-end",
        marginTop: rowGap,
        flexWrap: "wrap",
      }}
    >
      <Button
        label={showMoreFilters ? "Menos filtros" : "Más filtros"}
        icon={showMoreFilters ? "pi pi-chevron-up" : "pi pi-sliders-h"}
        outlined
        onClick={toggleMore}
      />
      <span style={{ flex: 1 }} />
      <Button
        label="Limpiar"
        severity="secondary"
        outlined
        onClick={clearAll}
      />
      <Button
        label="Aplicar"
        icon="pi pi-search"
        onClick={fetchDocumentsWithFilters}
      />
    </div>
  );

  const actionTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-eye"
        rounded
        outlined
        title="Ver detalles"
        onClick={() => navigate("/gestion", { state: { documento: rowData } })}
      />
      {/*<Button
        icon="pi pi-history"
        rounded
        outlined
        title="Historial de pagos"
      />*/}
    </div>
  );

  return (
    <div style={{ padding: "1rem" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontWeight: 700,
            color: styleProperties?.primaryText || "#2c2c2c",
          }}
        >
          Documentos
        </h2>

        <div
          style={{
            minWidth: 240,
            padding: "0.75rem 1rem",
            borderRadius: 12,
            background: "#f6f5f4",
            border: "1px solid #e0dddc",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
          }}
        >
          <span style={{ fontWeight: 600, color: "#4b4b4b" }}>
            Deuda Actual
          </span>
          <span style={{ fontWeight: 700, fontSize: "1.1rem" }}>
            {loadingDocs ? "Calculando…" : formatCurrency(totalDeudaCobranza)}
          </span>
        </div>
      </div>

      {/* Cuadro de filtros */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e0dddc",
          borderRadius: 12,
          padding: "1rem",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          marginBottom: "1rem",
        }}
      >
        {/* Fila filtros principal */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: "1rem",
            marginBottom: rowGap,
          }}
        >
          <div style={{ gridColumn: "span 4" }}>
            <label
              htmlFor="tipoDocumento"
              style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
            >
              Tipo de Documento
            </label>
            <Dropdown
              id="tipoDocumento"
              value={tipoDocumento}
              onChange={(e) => setTipoDocumento(e.value)}
              options={tipoDocumentos}
              optionLabel="label"
              optionValue="value"
              placeholder={
                loadingTipos ? "Cargando..." : "Selecciona tipo documento"
              }
              style={{ width: "100%" }}
              disabled={loadingTipos}
            />
          </div>

          <div style={{ gridColumn: "span 4" }}>
            <label
              htmlFor="cliente"
              style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
            >
              Cliente
            </label>
            <Dropdown
              id="cliente"
              value={cliente}
              onChange={(e) => setCliente(e.value)}
              options={clientes}
              optionLabel="label"
              optionValue="value"
              placeholder="Selecciona cliente"
              style={{ width: "100%" }}
            />
          </div>

          <DateRangeInline
            label="Fecha de emisión"
            fromId="emisionDesde"
            toId="emisionHasta"
            fromValue={fromDate}
            toValue={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
          />
        </div>

        {!showMoreFilters && <ButtonsRow />}

        {/* Filtros adicionales */}
        {showMoreFilters && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(12, 1fr)",
                gap: "1rem",
                marginTop: rowGap,
                marginBottom: rowGap,
              }}
            >
              <div style={{ gridColumn: "span 4" }}>
                <label
                  htmlFor="cobrador"
                  style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
                >
                  Cobrador
                </label>
                <Dropdown
                  id="cobrador"
                  value={cobrador}
                  onChange={(e) => setCobrador(e.value)}
                  options={cobradores}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Selecciona cobrador"
                  style={{ width: "100%" }}
                />
              </div>

              <div style={{ gridColumn: "span 4" }}>
                <label
                  htmlFor="numeroDocumento"
                  style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
                >
                  Número de documento
                </label>
                <InputText
                  id="numeroDocumento"
                  value={numeroDocumento}
                  onChange={(e) => setNumeroDocumento(e.target.value)}
                  placeholder="Ej: 123456"
                  type="number"
                  style={{ width: "100%" }}
                />
              </div>

              <DateRangeInline
                label="Fecha de compromiso"
                fromId="commitDesde"
                toId="commitHasta"
                fromValue={commitFromDate}
                toValue={commitToDate}
                onFromChange={setCommitFromDate}
                onToChange={setCommitToDate}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(12, 1fr)",
                gap: "1rem",
                marginTop: 0,
                marginBottom: rowGap,
              }}
            >
              {/*<div style={{ gridColumn: "span 4" }}>
                <label
                  htmlFor="estado"
                  style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
                >
                  Estado
                </label>
                <Dropdown
                  id="estado"
                  value={estado}
                  onChange={(e) => setEstado(e.value)}
                  options={estados}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={
                    loadingEstados ? "Cargando..." : "Selecciona estado"
                  }
                  style={{ width: "100%" }}
                  disabled={loadingEstados}
                />
              </div>*/}

              <DateRangeInline
                label="Fecha de vencimiento"
                fromId="vencDesde"
                toId="vencHasta"
                fromValue={vencFromDate}
                toValue={vencToDate}
                onFromChange={setVencFromDate}
                onToChange={setVencToDate}
                span={4}
                inputWidth={150}
              />
            </div>

            <ButtonsRow />
          </>
        )}
      </div>

      <TabView>
        <TabPanel header="💼 Cobranza">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1rem",
            }}
          >
            <Button
              label="Exportar a Excel"
              icon="pi pi-file-excel"
              severity="success"
              onClick={exportCobranzaExcel}
            />
          </div>
          <DataTable
            className="p-datatable-gridlines"
            ref={cobranzaRef}
            value={documentosCobranza}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 20, 50]}
            scrollable
            scrollHeight="500px"
            stripedRows
            loading={loadingCobranza}
          >
            <Column
              header="Acción"
              body={actionTemplate}
              style={{ width: "100px" }}
            />
            <Column field="cliente.rut" header="RUT" />
            <Column field="cliente.razonSocial" header="Cliente" />
            <Column field="tipoDocumento.descripcion" header="Tipo" />
            <Column field="folio" header="Folio" />
            <Column field="referencia" header="Referencia" body={(row) => row.referencia || "—"} />
            <Column
              field="fechaEmision"
              header="Fecha de Emisión"
              body={(row) => formatDateCell(row.fechaEmision)}
            />
            <Column
              field="fechaVencimiento"
              header="Fecha de Vencimiento"
              body={(row) => formatDateCell(row.fechaVencimiento)}
            />
            <Column field="dias" header="Días" />
            <Column
              field="fechaCompromiso"
              header="Fecha de Compromiso"
              body={(row) => formatDateCell(row.fechaCompromiso)}
            />
            <Column
              field="neto"
              header="Neto"
              body={(row) => formatCurrency(row.neto)}
            />
            <Column
              field="iva"
              header="IVA"
              body={(row) => formatCurrency(row.iva)}
            />
            <Column
              field="total"
              header="Total"
              body={(row) => formatCurrency(row.total)}
            />
            <Column
              field="deuda"
              header="Deuda"
              body={(row) => formatCurrency(row.deuda)}
            />
            <Column field="cobrador" header="Cobrador" />
          </DataTable>
        </TabPanel>

        <TabPanel header="📄 Pagados">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1rem",
            }}
          >
            <Button
              label="Exportar a Excel"
              icon="pi pi-file-excel"
              severity="success"
              onClick={exportPagadosExcel}
            />
          </div>
          <DataTable
            ref={pagadosRef}
            className="p-datatable-gridlines"
            value={documentosPagados}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 20, 50]}
            scrollable
            scrollHeight="500px"
            stripedRows
            loading={loadingPagados} // mismo spinner
          >
            <Column
              header="Acción"
              body={actionTemplate}
              style={{ width: "100px" }}
            />
            <Column field="cliente.rut" header="RUT" />
            <Column field="cliente.razonSocial" header="Cliente" />
            <Column field="tipoDocumento.descripcion" header="Tipo" />
            <Column field="folio" header="Folio" />
            <Column field="referencia" header="Referencia" body={(row) => row.referencia || "—"} />
            <Column field="fechaUltimoPago" header="Fecha de Pago" body={(row) => formatDateCell(row.fechaUltimoPago)} />
            <Column field="formaPago" header="Forma de Pago" />
            <Column
              field="fechaEmision"
              header="Fecha de Emisión"
              body={(row) => formatDateCell(row.fechaEmision)}
            />
            <Column
              field="fechaVencimiento"
              header="Fecha de Vencimiento"
              body={(row) => formatDateCell(row.fechaVencimiento)}
            />
            <Column field="dias" header="Días" />
            <Column
              field="fechaCompromiso"
              header="Fecha de Compromiso"
              body={(row) => formatDateCell(row.fechaCompromiso)}
            />
            <Column
              field="neto"
              header="Neto"
              body={(row) => formatCurrency(row.neto)}
            />
            <Column
              field="iva"
              header="IVA"
              body={(row) => formatCurrency(row.iva)}
            />
            <Column
              field="total"
              header="Total"
              body={(row) => formatCurrency(row.total)}
            />
            <Column
              field="deuda"
              header="Deuda"
              body={(row) => formatCurrency(row.deuda)}
            />
            <Column field="cobrador" header="Cobrador" />
          </DataTable>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default Documentos;
